// src/features/operational/components/teaching/LessonManagement.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FilterList as FilterIcon,
  MenuBook as AtpIcon,
  Search as SearchIcon,
  ViewModule as ModuleViewIcon,
  ViewWeek as WeekViewIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import { THEME_COLORS } from '../../../../styles/theme';
import AnnualTeachingPlan from '../assessment/AnnualTeachingPlan';
import LessonCalendar from './LessonCalendar';
import LessonForm from './LessonForm';
import LessonList from './LessonList';
import { fetchSubjects, fetchTeachingLessons } from '../../services/teachingService';

const LessonManagement = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lessons');
  const [lessons, setLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [viewMode, setViewMode] = useState('week');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    grade: '',
    term: '',
    week: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek());
  const [currentTerm, setCurrentTerm] = useState(getCurrentTerm());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [lessonsData, subjectsData] = await Promise.all([
        fetchTeachingLessons(currentUser.uid),
        fetchSubjects(currentUser.uid)
      ]);

      setLessons(lessonsData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading teaching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const filterLessons = useCallback(() => {
    let filtered = [...lessons];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((lesson) => (
        lesson.topic.toLowerCase().includes(searchLower)
        || lesson.subject.name.toLowerCase().includes(searchLower)
      ));
    }

    if (filters.subject) {
      filtered = filtered.filter((lesson) => lesson.subject.id === filters.subject);
    }

    if (filters.grade) {
      filtered = filtered.filter((lesson) => lesson.grade === filters.grade);
    }

    if (filters.term) {
      filtered = filtered.filter((lesson) => lesson.term === filters.term);
    }

    if (viewMode === 'week' && currentWeek) {
      const weekStart = new Date(currentWeek.start);
      const weekEnd = new Date(currentWeek.end);
      filtered = filtered.filter((lesson) => {
        const lessonDate = new Date(lesson.date);
        return lessonDate >= weekStart && lessonDate <= weekEnd;
      });
    } else if (viewMode === 'term' && currentTerm) {
      filtered = filtered.filter((lesson) => lesson.term === currentTerm.term.toString());
    }

    setFilteredLessons(filtered);
  }, [currentTerm, currentWeek, filters, lessons, searchTerm, viewMode]);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  useEffect(() => {
    filterLessons();
  }, [filterLessons]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      subject: '',
      grade: '',
      term: '',
      week: ''
    });
    setSearchTerm('');
  };

  const handleAddLesson = () => {
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
  };

  const handleFormSubmit = async () => {
    setIsFormOpen(false);
    await loadData();
  };

  function getCurrentWeek() {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);

    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(now);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return {
      start: monday.toISOString(),
      end: sunday.toISOString(),
      label: `Week ${getWeekNumber(monday)}`
    };
  }

  function getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  function getCurrentTerm() {
    const month = new Date().getMonth() + 1;

    let term;
    if (month >= 1 && month <= 3) term = 1;
    else if (month >= 4 && month <= 6) term = 2;
    else if (month >= 7 && month <= 9) term = 3;
    else term = 4;

    return {
      term,
      label: `Term ${term}`
    };
  }

  const navigatePrevious = () => {
    if (viewMode === 'week') {
      const prevWeekStart = new Date(currentWeek.start);
      prevWeekStart.setDate(prevWeekStart.getDate() - 7);

      const prevWeekEnd = new Date(prevWeekStart);
      prevWeekEnd.setDate(prevWeekStart.getDate() + 6);

      setCurrentWeek({
        start: prevWeekStart.toISOString(),
        end: prevWeekEnd.toISOString(),
        label: `Week ${getWeekNumber(prevWeekStart)}`
      });
    } else if (viewMode === 'term') {
      const prevTerm = currentTerm.term - 1;
      if (prevTerm >= 1) {
        setCurrentTerm({
          term: prevTerm,
          label: `Term ${prevTerm}`
        });
      }
    }
  };

  const navigateNext = () => {
    if (viewMode === 'week') {
      const nextWeekStart = new Date(currentWeek.start);
      nextWeekStart.setDate(nextWeekStart.getDate() + 7);

      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);

      setCurrentWeek({
        start: nextWeekStart.toISOString(),
        end: nextWeekEnd.toISOString(),
        label: `Week ${getWeekNumber(nextWeekStart)}`
      });
    } else if (viewMode === 'term') {
      const nextTerm = currentTerm.term + 1;
      if (nextTerm <= 4) {
        setCurrentTerm({
          term: nextTerm,
          label: `Term ${nextTerm}`
        });
      }
    }
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            {activeTab === 'lessons' ? 'Lesson Management' : 'Annual Teaching Plan'}
          </Typography>
          {activeTab === 'lessons' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddLesson}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Record Lesson
            </Button>
          )}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{
            mb: activeTab === 'lessons' ? 3 : 0,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 600
            },
            '& .Mui-selected': {
              color: THEME_COLORS.primary
            },
            '& .MuiTabs-indicator': {
              backgroundColor: THEME_COLORS.primary
            }
          }}
        >
          <Tab icon={<CalendarIcon />} iconPosition="start" label="Lessons" value="lessons" />
          <Tab icon={<AtpIcon />} iconPosition="start" label="ATP" value="atp" />
        </Tabs>

        {activeTab === 'lessons' && (
          <>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Box sx={{ position: 'relative', flexGrow: 1 }}>
                <SearchIcon
                  sx={{
                    position: 'absolute',
                    left: 1,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    ml: 1,
                    color: 'text.secondary'
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Search lessons by topic or subject..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      paddingLeft: '35px'
                    }
                  }}
                />
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    borderColor: THEME_COLORS.primary,
                    color: THEME_COLORS.primary,
                    '&:hover': {
                      borderColor: '#1e1c4e',
                      backgroundColor: 'rgba(39, 35, 97, 0.04)'
                    }
                  }}
                >
                  Filters
                </Button>

                <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Button
                    variant={viewMode === 'week' ? 'contained' : 'text'}
                    onClick={() => setViewMode('week')}
                    sx={{
                      minWidth: '40px',
                      backgroundColor: viewMode === 'week' ? THEME_COLORS.primary : 'transparent',
                      color: viewMode === 'week' ? 'white' : THEME_COLORS.primary,
                      '&:hover': {
                        backgroundColor: viewMode === 'week' ? '#1e1c4e' : 'rgba(39, 35, 97, 0.04)'
                      }
                    }}
                  >
                    <WeekViewIcon fontSize="small" />
                  </Button>
                  <Button
                    variant={viewMode === 'term' ? 'contained' : 'text'}
                    onClick={() => setViewMode('term')}
                    sx={{
                      minWidth: '40px',
                      backgroundColor: viewMode === 'term' ? THEME_COLORS.primary : 'transparent',
                      color: viewMode === 'term' ? 'white' : THEME_COLORS.primary,
                      '&:hover': {
                        backgroundColor: viewMode === 'term' ? '#1e1c4e' : 'rgba(39, 35, 97, 0.04)'
                      }
                    }}
                  >
                    <ModuleViewIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>

            {showFilters && (
              <Box mt={2} pt={2} borderTop="1px solid #e0e0e0">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Subject</InputLabel>
                      <Select name="subject" value={filters.subject} label="Subject" onChange={handleFilterChange}>
                        <MenuItem value="">All Subjects</MenuItem>
                        {subjects.map((subject) => (
                          <MenuItem key={subject.id} value={subject.id}>
                            {subject.name} - Grade {subject.grade}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Grade</InputLabel>
                      <Select name="grade" value={filters.grade} label="Grade" onChange={handleFilterChange}>
                        <MenuItem value="">All Grades</MenuItem>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((grade) => (
                          <MenuItem key={grade} value={grade.toString()}>
                            Grade {grade}
                          </MenuItem>
                        ))}
                        <MenuItem value="R">Grade R</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Term</InputLabel>
                      <Select name="term" value={filters.term} label="Term" onChange={handleFilterChange}>
                        <MenuItem value="">All Terms</MenuItem>
                        <MenuItem value="1">Term 1</MenuItem>
                        <MenuItem value="2">Term 2</MenuItem>
                        <MenuItem value="3">Term 3</MenuItem>
                        <MenuItem value="4">Term 4</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3} display="flex" justifyContent="flex-end" alignItems="center">
                    <Button variant="text" onClick={clearFilters} sx={{ color: THEME_COLORS.primary }}>
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </>
        )}
      </Paper>

      {activeTab === 'lessons' ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <IconButton onClick={navigatePrevious}>
              <ChevronLeftIcon />
            </IconButton>

            <Typography variant="h6" fontWeight="medium">
              {viewMode === 'week'
                ? `${currentWeek.label} - ${new Date(currentWeek.start).toLocaleDateString()} - ${new Date(currentWeek.end).toLocaleDateString()}`
                : currentTerm.label}
            </Typography>

            <IconButton onClick={navigateNext}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress sx={{ color: THEME_COLORS.primary }} />
            </Box>
          ) : (
            <>
              {viewMode === 'week' ? (
                <LessonCalendar lessons={filteredLessons} currentWeek={currentWeek} onAddLesson={handleAddLesson} />
              ) : (
                <LessonList lessons={filteredLessons} onAddLesson={handleAddLesson} />
              )}
            </>
          )}
        </>
      ) : (
        <AnnualTeachingPlan />
      )}

      <LessonForm
        open={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        subjects={subjects}
      />
    </Box>
  );
};

export default LessonManagement;
